import i18n from "../i18n";
import { useState, useEffect, useRef, useMemo } from "react";
import {
  Badge,
  Button,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import _ from "lodash";
import { useServerApi } from "../hooks/userServerApi";
import { useFormatter } from "../hooks/useFomatter";
import {
  getConsultantLabel,
  getPayrollKey,
  getPayrollLabel,
} from "../helpers/payrollBidDisplay";

const PAGE_SIZE = 200;

const getPayrollBidStatusColor = (status) => {
  switch (status) {
    case "SETTLED":
      return "green";
    case "HOLD":
      return "yellow";
    case "CANCEL":
      return "red";
    case "PENDING":
    default:
      return "gray";
  }
};

const getLegacyBidStatusColor = (status) => {
  switch (status) {
    case "SETTLED":
      return "green";
    case "RELEASED":
      return "violet";
    case "FULFILLED":
      return "cyan";
    case "TERMINATED":
      return "red";
    case "OPENING":
    default:
      return "gray";
  }
};

const getStatusColor = (status, isLegacy) =>
  isLegacy ? getLegacyBidStatusColor(status) : getPayrollBidStatusColor(status);

const getBidAmount = (bid, isLegacy) =>
  isLegacy
    ? bid.paidAmount ?? bid.expectedAmount ?? 0
    : bid.commissionAmount ?? 0;

const buildStatusSummary = (bids, isLegacy) => {
  const counts = isLegacy
    ? { OPENING: 0, FULFILLED: 0, RELEASED: 0, SETTLED: 0, TERMINATED: 0 }
    : { PENDING: 0, HOLD: 0, SETTLED: 0, CANCEL: 0 };
  let totalAmount = 0;

  for (const bid of bids) {
    const status = bid.status || (isLegacy ? "OPENING" : "PENDING");
    if (counts[status] != null) counts[status] += 1;
    totalAmount += getBidAmount(bid, isLegacy);
  }

  return { counts, totalAmount, total: bids.length };
};

const normalizePayrollBidGroups = (bids) => {
  const grouped = _.groupBy(bids, getPayrollKey);

  return Object.entries(grouped)
    .map(([key, payrollBids]) => ({
      key,
      label: getPayrollLabel(payrollBids[0]),
      bids: payrollBids,
      isLegacy: false,
      totalAmount: payrollBids.reduce(
        (sum, bid) => sum + getBidAmount(bid, false),
        0,
      ),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const normalizeLegacyBidGroups = (apiResponse) => {
  const groups = apiResponse?.payrollGroups ?? [];
  return groups.map((group) => ({
    key: group.payroll?._id?.toString?.() ?? group.payroll?.code ?? "unknown",
    label: group.payroll?.code || "Not released",
    bids: group.bids ?? [],
    isLegacy: true,
    totalAmount: (group.bids ?? []).reduce(
      (sum, bid) => sum + getBidAmount(bid, true),
      0,
    ),
  }));
};

const BidIdentityCell = ({ bid, isLegacy }) => {
  const payrollCode = getPayrollLabel(bid);
  const status = bid.status || (isLegacy ? "OPENING" : "PENDING");

  return (
    <Stack spacing={4}>
      <Group spacing={6} noWrap>
        <Badge size="xs" color={getStatusColor(status, isLegacy)}>
          {status}
        </Badge>
        <Text size="xs" weight={600}>
          {payrollCode}
        </Text>
      </Group>
      <Text size="xs" color="dimmed" lineClamp={2}>
        {bid.code || "-"}
      </Text>
    </Stack>
  );
};

const PayrollBidsTable = ({ bids, formatter, isLegacy }) => (
  <Table fontSize="xs" striped highlightOnHover withBorder>
    <thead>
      <tr>
        <th style={{ width: 36 }}>#</th>
        <th>{i18n.t("Payroll / Bid")}</th>
        <th>{i18n.t("Consultant / Wallet")}</th>
        <th>{i18n.t("Tree / Level")}</th>
        <th style={{ textAlign: "right" }}>{i18n.t("Amount")}</th>
      </tr>
    </thead>
    <tbody>
      {bids.map((bid, index) => {
        const amount = getBidAmount(bid, isLegacy);
        return (
          <tr key={bid._id || index}>
            <td>{index + 1}</td>
            <td>
              <BidIdentityCell bid={bid} isLegacy={isLegacy} />
            </td>
            <td>
              <Text size="xs">{getConsultantLabel(bid)}</Text>
            </td>
            <td>
              <Text size="xs">
                T{bid.tree ?? 0} / L{bid.level ?? 0}
              </Text>
            </td>
            <td style={{ textAlign: "right" }}>
              <Text
                size="xs"
                weight={500}
                color={amount >= 0 ? "green" : "red"}
              >
                {formatter.currency(amount)}
              </Text>
            </td>
          </tr>
        );
      })}
    </tbody>
  </Table>
);

const resolveOrderId = async (api, formOrderId, debitNoteId) => {
  if (formOrderId) return formOrderId;

  if (!debitNoteId) return null;

  const result = await api.search({
    apiEntity: "order",
    pageSize: 1,
    searchQuery: { debitNote: debitNoteId },
    byAggregation: false,
  });

  return result?.docs?.[0]?._id ?? null;
};

const DebitNotePayrollBidsSection = ({ form }) => {
  const debitNoteId = form?.values?._id?.toString?.() ?? form?.values?._id;
  const formOrderId =
    _.get(form?.values, "order._id")?.toString?.() ??
    _.get(form?.values, "order._id");

  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [bids, setBids] = useState([]);
  const [isLegacy, setIsLegacy] = useState(false);
  const [payrollGroupsFromApi, setPayrollGroupsFromApi] = useState(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const apiRef = useRef(api);
  apiRef.current = api;

  useEffect(() => {
    if (!debitNoteId) {
      setBids([]);
      setTotalDocs(0);
      setCurrentPage(1);
      setIsLegacy(false);
      setPayrollGroupsFromApi(null);
      return;
    }

    let cancelled = false;

    const loadBids = async () => {
      setInitialLoading(true);
      try {
        const orderId = await resolveOrderId(
          apiRef.current,
          formOrderId,
          debitNoteId,
        );

        const payrollResult =
          await apiRef.current.Payroll2.getPayrollBidsByDebitNote(debitNoteId, {
            orderId,
            page: 1,
            limit: PAGE_SIZE,
          });

        if (cancelled) return;

        const payrollBids = payrollResult?.success
          ? payrollResult.data?.bids ?? []
          : [];

        if (payrollBids.length > 0) {
          setIsLegacy(false);
          setPayrollGroupsFromApi(null);
          setBids(payrollBids);
          setTotalDocs(payrollResult?.data?.total ?? payrollBids.length);
          setCurrentPage(1);
          return;
        }

        const legacyResult = await apiRef.current.Bid.getByDebitNote(
          debitNoteId,
          { orderId },
        );

        if (cancelled) return;

        if (legacyResult?.success) {
          const legacyBids = legacyResult.data?.bids ?? [];
          setIsLegacy(true);
          setPayrollGroupsFromApi(legacyResult.data ?? null);
          setBids(legacyBids);
          setTotalDocs(legacyResult.data?.total ?? legacyBids.length);
          setCurrentPage(1);
        } else {
          setIsLegacy(false);
          setPayrollGroupsFromApi(null);
          setBids([]);
          setTotalDocs(0);
          setCurrentPage(1);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching payroll bids:", error);
          setBids([]);
          setTotalDocs(0);
          setIsLegacy(false);
          setPayrollGroupsFromApi(null);
        }
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    };

    loadBids();

    return () => {
      cancelled = true;
    };
  }, [debitNoteId]);

  const loadMore = async () => {
    if (!debitNoteId || loadingMore || isLegacy) return;

    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const orderId = await resolveOrderId(
        apiRef.current,
        formOrderId,
        debitNoteId,
      );
      const result = await apiRef.current.Payroll2.getPayrollBidsByDebitNote(
        debitNoteId,
        {
          orderId,
          page: nextPage,
          limit: PAGE_SIZE,
        },
      );

      const docs = result?.success ? result.data?.bids ?? [] : [];
      setBids((prev) => [...prev, ...docs]);
      setCurrentPage(nextPage);
      setTotalDocs(result?.data?.total ?? totalDocs);
    } catch (error) {
      console.error("Error loading more payroll bids:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const payrollGroups = useMemo(() => {
    if (isLegacy && payrollGroupsFromApi) {
      return normalizeLegacyBidGroups(payrollGroupsFromApi);
    }
    return normalizePayrollBidGroups(bids);
  }, [bids, isLegacy, payrollGroupsFromApi]);

  const summary = useMemo(
    () => buildStatusSummary(bids, isLegacy),
    [bids, isLegacy],
  );
  const hasMore = !isLegacy && bids.length < totalDocs;

  if (!form?.values) {
    return (
      <Group position="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  }

  if (!debitNoteId) {
    return (
      <Text size="sm" color="dimmed">
        Save the debit note to view payroll bids.
      </Text>
    );
  }

  if (initialLoading && bids.length === 0) {
    return (
      <Group position="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack spacing="md">
      <Group spacing="lg">
        <Text size="sm">
          <Text component="span" color="dimmed">
            Total bids:{" "}
          </Text>
          <Text component="span" weight={600}>
            {summary.total}
          </Text>
        </Text>
        <Text size="sm">
          <Text component="span" color="dimmed">
            Total amount:{" "}
          </Text>
          <Text component="span" weight={600}>
            {formatter.currency(summary.totalAmount)}
          </Text>
        </Text>
        {Object.entries(summary.counts).map(([status, count]) =>
          count > 0 ? (
            <Badge
              key={status}
              size="sm"
              color={getStatusColor(status, isLegacy)}
            >
              {status}: {count}
            </Badge>
          ) : null,
        )}
      </Group>

      {bids.length === 0 ? (
        <Text size="sm" color="dimmed">
          No payroll bids found for this debit note.
        </Text>
      ) : (
        <>
          {payrollGroups.map((group) => (
            <Stack key={group.key} spacing="xs">
              <Group position="apart">
                <Group spacing="xs">
                  <Title order={6}>{group.label}</Title>
                  {group.isLegacy && (
                    <Badge size="xs" color="orange" variant="light">
                      Legacy Payroll
                    </Badge>
                  )}
                </Group>
                <Text size="xs" color="dimmed">
                  {group.bids.length} bid(s) ·{" "}
                  {formatter.currency(group.totalAmount)}
                </Text>
              </Group>
              <PayrollBidsTable
                bids={group.bids}
                formatter={formatter}
                isLegacy={group.isLegacy}
              />
            </Stack>
          ))}

          {hasMore && (
            <Group position="center">
              <Button
                size="xs"
                variant="light"
                loading={loadingMore}
                onClick={loadMore}
              >
                Load more ({bids.length} of {totalDocs})
              </Button>
            </Group>
          )}
        </>
      )}
    </Stack>
  );
};

export default DebitNotePayrollBidsSection;
